import { FC, useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import Wavesurfer from 'wavesurfer.js';

import { getDurationMSS } from '../../lib/time';
import { logger } from '../../../lib/logger';

import Spinner from '../../svgs/Spinner';

import { useWaveSurfer } from './useWaveSurfer';
import PlayPauseButton from './PlayPauseButton';

const PROGRESS_UPDATE_INTERVAL_MS = 50;

const AudioPlayer: FC<{
  playerId: string;
  duration?: number | null;
  mediaUrl?: string;
  audioBlob?: Blob | undefined;
  className?: string;
  children?: React.ReactNode;
  autoPlay?: boolean;
  defaultStyle?: boolean;
  triggerStop?: boolean;
  onReady?: (wavesurfer: Wavesurfer) => void;
}> = ({
  playerId,
  duration = 0,
  mediaUrl,
  audioBlob,
  className,
  children,
  autoPlay = false,
  defaultStyle = true,
  triggerStop,
  onReady,
}) => {
  // Prefix player ID with 'player-' to ensure that IDs that start with a nondigit char
  // Fix for this error https://stackoverflow.com/questions/37270787/uncaught-syntaxerror-failed-to-execute-queryselector-on-document
  playerId = 'player-' + playerId;
  const wavesurfer = useWaveSurfer(playerId);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlayable, setIsPlayable] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [secondsRemaining, setSecondsRemaining] = useState(duration);

  // Keep refs to props that are used in useEffect callbacks to avoid stale closures
  const durationRef = useRef(duration);
  const autoPlayRef = useRef(autoPlay);
  const onReadyRef = useRef(onReady);

  // Update refs when props change
  useEffect(() => {
    durationRef.current = duration;
    autoPlayRef.current = autoPlay;
    onReadyRef.current = onReady;
  }, [duration, autoPlay, onReady]);

  useEffect(() => {
    if (wavesurfer) {
      if (mediaUrl) {
        wavesurfer.load(mediaUrl);
      } else if (audioBlob) {
        wavesurfer.loadBlob(audioBlob);
      }
      return () => {
        wavesurfer?.pause(); // pause on unmount
        setIsPlaying(false);
      };
    }
  }, [wavesurfer, mediaUrl, audioBlob]);

  useEffect(() => {
    if (isPlaying && triggerStop) {
      wavesurfer?.pause();
    }
  }, [triggerStop]);

  useEffect(() => {
    if (duration) {
      setSecondsElapsed(0);
      setSecondsRemaining(duration);
    }
  }, [duration]);

  const playbackIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  // Register all wavesurfer event listeners in a single effect so they are
  // attached once (when the instance is created) and cleaned up on unmount.
  // Previously these lived in the render body, which re-registered a new set
  // of listeners on every render — causing duplicate callbacks, leaked
  // intervals, and eventual "Maximum update depth exceeded" crashes.
  useEffect(() => {
    if (!wavesurfer) return;

    const handleReady = () => {
      setIsPlayable(true);
      setHasError(false);
      if (autoPlayRef.current) {
        wavesurfer.play();
        setIsPlaying(true);
      }
      onReadyRef.current?.(wavesurfer);
    };
    const handlePlay = () => {
      if (playbackIntervalRef.current)
        clearInterval(playbackIntervalRef.current);
      playbackIntervalRef.current = setInterval(() => {
        const currentTimeInSeconds = Math.round(wavesurfer.getCurrentTime());
        setSecondsElapsed(currentTimeInSeconds);
        if (durationRef.current)
          setSecondsRemaining(durationRef.current - currentTimeInSeconds);
      }, PROGRESS_UPDATE_INTERVAL_MS);
    };
    const handlePause = () => {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
        playbackIntervalRef.current = null;
      }
    };
    const handleFinish = () => {
      setIsPlaying(false);
    };
    const handleSeek = (progress: number) => {
      if (durationRef.current) {
        const currentTimeInSeconds = Math.ceil(progress * durationRef.current);
        setSecondsElapsed(currentTimeInSeconds);
        setSecondsRemaining(durationRef.current - currentTimeInSeconds);
      }
    };
    // Guard against media decode/network errors so a corrupt blob or blocked
    // URL doesn't leave the player stuck on the spinner forever.
    const handleError = (e: unknown) => {
      setIsPlayable(false);
      setHasError(true);
      setIsPlaying(false);
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
        playbackIntervalRef.current = null;
      }
      logger.warn('WaveSurfer failed to load audio', { error: e });
    };

    wavesurfer.on('ready', handleReady);
    wavesurfer.on('play', handlePlay);
    wavesurfer.on('pause', handlePause);
    wavesurfer.on('finish', handleFinish);
    wavesurfer.on('seek', handleSeek);
    wavesurfer.on('error', handleError);

    return () => {
      wavesurfer.un('ready', handleReady);
      wavesurfer.un('play', handlePlay);
      wavesurfer.un('pause', handlePause);
      wavesurfer.un('finish', handleFinish);
      wavesurfer.un('seek', handleSeek);
      wavesurfer.un('error', handleError);
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
        playbackIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Intentionally omitting durationRef, autoPlayRef, onReadyRef, and playbackIntervalRef from deps.
    // These refs are used inside event handlers to always access current values without
    // re-registering listeners when props change. The listeners only depend on wavesurfer instance.
  }, [wavesurfer]);

  const onClickPlayPauseButton = useCallback(async () => {
    wavesurfer?.playPause();
    setIsPlaying(!isPlaying);
  }, [wavesurfer, isPlaying]);

  return (
    <div
      className={classNames(
        'relative flex w-full flex-row items-center justify-between',
        'rounded-lg border border-neutral-50',
        defaultStyle && 'h-[48px] px-4 py-8',
        className,
      )}
    >
      <PlayPauseButton
        isPlayable={isPlayable}
        isPlaying={isPlaying}
        onClick={onClickPlayPauseButton}
      />
      <div
        className={classNames(
          'text-netural-150 mr-1 min-w-[40px] text-caption',
          !isPlayable && 'text-neutral-100',
        )}
      >
        {getDurationMSS(secondsElapsed)}
      </div>
      <div id={playerId} className="w-full" />
      <div
        className={classNames(
          'text-netural-150 ml-3 min-w-[40px] text-caption',
          !isPlayable && 'text-neutral-100',
        )}
      >
        {getDurationMSS(secondsRemaining)}
      </div>
      {!isPlayable && !hasError && (
        <div className="absolute top-0 left-0 flex h-full w-full flex-row items-center justify-center">
          <Spinner className="h-4 w-4 text-neutral-100" />
        </div>
      )}
      {children}
    </div>
  );
};

export default AudioPlayer;
